import { createAction } from '@reduxjs/toolkit';
import { ErrorPayload } from '../types';
import { BleControllerBundle, BleControllerDevice, BleControllerFile } from './types';

const acknowledgeAndClearErrors = createAction('BLE.CLEAR_ERRORS');

/** An action that is dispatched when the controller emits its serial number. */
const updateSerialNumber = createAction<string>('BLE.UPDATE_SERIAL_NUMBER');
/** An action that is dispatched when the controller emits its firmware version. */
const updateFirmwareVersion = createAction<string>('BLE.UPDATE_FIRMWARE_VERSION');

/** An action that is dispatched when the application begins scanning for BLE devices. */
const scanDevicesStart = createAction('BLE.SCAN_DEVICES.START');

interface ScanDevicesProgressPayload {
  device: BleControllerDevice;
}
/** An action that is dispatched when a BLE device is discovered during the scan. */
const scanDevicesProgress = createAction<ScanDevicesProgressPayload>('BLE.SCAN_DEVICES.PROGRESS');

/** An action that is dispatched when the BLE device scan completes successfully. */
const scanDevicesSuccess = createAction('BLE.SCAN_DEVICES.SUCCESS');

interface ScanDevicesFailurePayload extends ErrorPayload {}
/** An action that is dispatched when the BLE device scan completes unsuccessfully. */
const scanDevicesFailure = createAction<ScanDevicesFailurePayload>('BLE.SCAN_DEVICES.FAILURE');

/** An action that is dispatched when the application begins connecting to a BLE device. */
const connectDeviceStart = createAction('BLE.CONNECT_DEVICE.START');

interface ConnectDeviceSuccessPayload {
  device: BleControllerDevice;
}
/** An action that is dispatched when the BLE device connection completes successfully. */
const connectDeviceSuccess = createAction<ConnectDeviceSuccessPayload>('BLE.CONNECT_DEVICE.SUCCESS');

interface ConnectDeviceFailurePayload extends ErrorPayload {}
/** An action that is dispatched when the BLE device connection completes unsuccessfully. */
const connectDeviceFailure = createAction<ConnectDeviceFailurePayload>('BLE.CONNECT_DEVICE.FAILURE');

interface DisconnectDevicePayload extends ErrorPayload {}
/** An action that is dispatched when a BLE device disconnects. */
const disconnectDevice = createAction<DisconnectDevicePayload>('BLE.DISCONNECT_DEVICE');

/** An action that is dispatched when the application begins deleting a log file on the BLE device. */
const deleteLogFileStart = createAction('BLE.DELETE_LOG_FILE.START');

/** An action that is dispatched when the application completes deleting a log file on the BLE device successfully. */
const deleteLogFileSuccess = createAction('BLE.DELETE_LOG_FILE.SUCCESS');

interface DeleteLogFileFailurePayload extends ErrorPayload {}
/** An action that is dispatched when the application completes deleting a log file on the BLE device unsuccessfully. */
const deleteLogFileFailure = createAction<DeleteLogFileFailurePayload>('BLE.DELETE_LOG_FILE.FAILURE');

interface ReadLogFileStartPayload {
  /** The file that is being read. */
  filename: string;
}
/** An action that is dispatched when the application begins reading a log file on the BLE device. */
const readLogFileStart = createAction<ReadLogFileStartPayload>('BLE.READ_LOG_FILE.START');

interface ReadLogFileProgressPayload {
  /** The number of bytes in the file. */
  totalBytes: number;
  /** The number of bytes that have been written. */
  readBytes: number;
}

/** An action that is dispatched when the application makes progress reading a log file on the BLE device. */
const readLogFileProgress = createAction<ReadLogFileProgressPayload>('BLE.READ_LOG_FILE.PROGRESS');

interface ReadLogFileSuccessPayload {
  /** The full log data. */
  data: string;
}
/** An action that is dispatched when the application completes reading a log file on the BLE device successfully. */
const readLogFileSuccess = createAction<ReadLogFileSuccessPayload>('BLE.READ_LOG_FILE.SUCCESS');

interface ReadLogFileFailurePayload extends ErrorPayload {}
/** An action that is dispatched when the application completes reading a log file on the BLE device unsuccessfully. */
const readLogFileFailure = createAction<ReadLogFileFailurePayload>('BLE.READ_LOG_FILE.FAILURE');

interface WriteLogFileStartPayload {
  /** The number of bytes in the file. */
  totalBytes: number;
}
/** An action that is dispatched when the application begins writing a log file on the BLE device. */
const writeLogFileStart = createAction<WriteLogFileStartPayload>('BLE.WRITE_LOG_FILE.START');

interface WriteLogFileProgressPayload {
  /** The number of bytes that have been written. */
  writtenBytes: number;
}
/** An action that is dispatched when the application makes progress writing a log file on the BLE device. */
const writeLogFileProgress = createAction<WriteLogFileProgressPayload>('BLE.WRITE_LOG_FILE.PROGRESS');

/** An action that is dispatched when the application completes writing a log file on the BLE device successfully. */
const writeLogFileSuccess = createAction('BLE.WRITE_LOG_FILE.SUCCESS');

interface WriteLogFileFailurePayload extends ErrorPayload {}
/** An action that is dispatched when the application completes writing a log file on the BLE device unsuccessfully. */
const writeLogFileFailure = createAction<WriteLogFileFailurePayload>('BLE.WRITE_LOG_FILE.FAILURE');

/** An action that is dispatched when the application begins listing log files on the BLE device. */
const listLogFilesStart = createAction('BLE.LIST_LOG_FILES.START');

interface ListLogFilesSuccessPayload {
  data: BleControllerFile[];
}
/** An action that is dispatched when the application completes listing log files on the BLE device successfully. */
const listLogFilesSuccess = createAction<ListLogFilesSuccessPayload>('BLE.LIST_LOG_FILES.SUCCESS');

interface ListLogFilesErrorPayload extends ErrorPayload {}
/** An action that is dispatched when the application completes listing log files on the BLE device unsuccessfully. */
const listLogFilesFailure = createAction<ListLogFilesErrorPayload>('BLE.LIST_LOG_FILES.FAILURE');

interface SyncLogFileStartPayload {
  /** The filename of the log being synced. */
  filename: string;
}

/** An action that is dispatched when the application begins reading a log file from the BLE device. */
const syncLogFileStart = createAction<SyncLogFileStartPayload>('BLE.SYNC_LOG_FILE.START');

interface SyncLogFileProgressPayload {
  /** The current number of uploaded lines in the job. */
  uploadedBytes: number;
  /** The total number of lines in the job. */
  totalBytes: number;
}

/** An action that is dispatched when the application makes progress uploading a log file from the BLE device. */
const syncLogFileProgress = createAction<SyncLogFileProgressPayload>('BLE.SYNC_LOG_FILE.PROGRESS');

/** An action that is dispatched when the application completes reading a log file from the BLE device and successfully uploads it to the sentinel service. */
const syncLogFileSuccess = createAction('BLE.SYNC_LOG_FILE.SUCCESS');

interface SyncLogFileErrorPayload extends ErrorPayload {}
/** An action that is dispatched when the application unsuccessfully completes reading a log file from the BLE device. */
const syncLogFileFailure = createAction<SyncLogFileErrorPayload>('BLE.SYNC_LOG_FILE.FAILURE');

/** An action that is dispatched when the sync log file state should be reset. */
const syncLogFileReset = createAction('BLE.SYNC_LOG_FILE.RESET');

/** An action that is dispatched when application starts updating firmware from the BLE device */
const updateFirmwareStart = createAction('BLE.UPDATE_FIRMWARE.START');

interface UpdateFirmwareProgressPayload {
  /** The number of bytes that have been written. */
  writtenBytes: number;
  /** The total number of bytes that have been written. */
  totalBytes: number;
}

/** An action that is dispatched when the application makes progress updating firmware on BLE device. */
const updateFirmwareProgress = createAction<UpdateFirmwareProgressPayload>('BLE.UPDATE_FIRMWARE.PROGRESS');

/** An action that is dispatched when firmware update completes successfully. */
const updateFirmwareSuccess = createAction('BLE.UPDATE_FIRMWARE.SUCCESS');

interface UpdateFirmwareFailurePayload extends ErrorPayload {
  errorCode: 'fetchingFirmware' | 'assigningFirmwareLicense' | 'installingFirmware';
}
/** An action that is dispatched when firmware update completes unsuccessfully. */
const updateFirmwareFailure = createAction<UpdateFirmwareFailurePayload>('BLE.UPDATE_FIRMWARE.FAILURE');

/** An action that is dispatched when listing the bundles on the controller begins. */
const listBundlesStart = createAction('BLE.LIST_BUNDLES.START');

interface ListBundlesSuccessPayload {
  /** The list of bundle files on the controller. */
  data: BleControllerBundle[];
}

/** An action that is dispatched when listing the bundles on the controller completes successfully. */
const listBundlesSuccess = createAction<ListBundlesSuccessPayload>('BLE.LIST_BUNDLES.SUCCESS');

/** An action that is dispatched when listing the bundles on the controller completes unsuccessfully. */
const listBundlesFailure = createAction<ErrorPayload>('BLE.LIST_BUNDLES.FAILURE');

interface WriteBundleStartPayload {
  /** The id of the bundle being written. */
  bundleId: string;
  /** The total number of bytes in the file. */
  totalBytes: number;
}

/** An action that is dispatched when writing a bundle to a controller begins. */
const writeBundleStart = createAction<WriteBundleStartPayload>('BLE.WRITE_BUNDLE.START');

interface WriteBundleProgressPayload {
  /** The number of bytes that have been written so far. */
  writtenBytes: number;
}

/** An action that is dispatched when writing a bundle to a controller makes progress. */
const writeBundleProgress = createAction<WriteBundleProgressPayload>('BLE.WRITE_BUNDLE.PROGRESS');

interface WriteBundleSuccessPayload {
  /** The bundle file that was written to the controller. */
  data: BleControllerBundle;
}

/** An action that is dispatched when writing a bundle to a controller completes successfully. */
const writeBundleSuccess = createAction<WriteBundleSuccessPayload>('BLE.WRITE_BUNDLE.SUCCESS');

/** An action that is dispatched when writing a bundle to a controller completes unsuccessfully. */
const writeBundleFailure = createAction<ErrorPayload>('BLE.WRITE_BUNDLE.FAILURE');

/** An action that is dispatched when deleting a bundle from a controller begins. */
const deleteBundleStart = createAction('BLE.DELETE_BUNDLE.START');

interface DeleteBundleSuccessPayload {
  /** The id of the bundle that was deleted. */
  bundleId: string;
}

/** An action that is dispatched when deleting a bundle from a controller completes successfully. */
const deleteBundleSuccess = createAction<DeleteBundleSuccessPayload>('BLE.DELETE_BUNDLE.SUCCESS');

/** An action that is dispatched when deleting a bundle from a controller completes unsuccessfully. */
const deleteBundleFailure = createAction<ErrorPayload>('BLE.DELETE_BUNDLE.FAILURE');

/** An action that is dispatched when a SA Dev dismissed the create controller dialog. */
const dismissCreateController = createAction('BLE.CREATE_CONTROLLER.DISMISS');

/** An action that is dispatched when the user opts to connect to a controller  */
const openConnectControllerModal = createAction('BLE.OPEN_CONNECT_CONTROLLER_MODAL');

/**
 * Creates an action to be dispatched when the user opts to close the connect controller modal.
 */
const closeConnectControllerModal = createAction('BLE.CLOSE_CONNECT_CONTROLLER_MODAL');

export const bleActions = {
  acknowledgeAndClearErrors,
  updateFirmwareVersion,
  updateSerialNumber,
  scanDevicesStart,
  scanDevicesProgress,
  scanDevicesSuccess,
  scanDevicesFailure,
  connectDeviceStart,
  connectDeviceSuccess,
  connectDeviceFailure,
  disconnectDevice,
  deleteLogFileStart,
  deleteLogFileSuccess,
  deleteLogFileFailure,
  readLogFileStart,
  readLogFileProgress,
  readLogFileSuccess,
  readLogFileFailure,
  writeLogFileStart,
  writeLogFileSuccess,
  writeLogFileProgress,
  writeLogFileFailure,
  listLogFilesStart,
  listLogFilesSuccess,
  listLogFilesFailure,
  syncLogFileStart,
  syncLogFileSuccess,
  syncLogFileFailure,
  syncLogFileProgress,
  syncLogFileReset,
  updateFirmwareStart,
  updateFirmwareProgress,
  updateFirmwareSuccess,
  updateFirmwareFailure,
  listBundlesStart,
  listBundlesSuccess,
  listBundlesFailure,
  writeBundleStart,
  writeBundleProgress,
  writeBundleSuccess,
  writeBundleFailure,
  deleteBundleStart,
  deleteBundleSuccess,
  deleteBundleFailure,
  dismissCreateController,
  openConnectControllerModal,
  closeConnectControllerModal,
};
